import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import SessionExpiredModal from '../components/SessionExpiredModal';
import { installAuthInterceptor, setAxiosAuthToken } from '../utils/authClient';
import { authenticateRealtime, signOutRealtime } from '../utils/socket';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const activityHeartbeatRegistry = new Map();

// Keep one heartbeat timer per user within the application runtime, even if
// development remounts or multiple AuthProvider instances occur briefly.
const acquireActivityHeartbeat = (userId) => {
  const current = activityHeartbeatRegistry.get(userId);
  if (current) {
    current.refCount += 1;
    return () => {
      current.refCount -= 1;
      if (current.refCount <= 0) {
        clearInterval(current.interval);
        activityHeartbeatRegistry.delete(userId);
      }
    };
  }

  const updateLastSeen = async () => {
    try {
      await axios.post(`${API}/admin/activity/track`, {
        user_id: userId,
        action: 'heartbeat'
      });
    } catch (error) {
      console.error('Heartbeat failed:', error);
    }
  };

  const interval = setInterval(updateLastSeen, 30000);
  activityHeartbeatRegistry.set(userId, { interval, refCount: 1 });
  updateLastSeen();

  return () => {
    const registered = activityHeartbeatRegistry.get(userId);
    if (!registered) return;
    registered.refCount -= 1;
    if (registered.refCount <= 0) {
      clearInterval(registered.interval);
      activityHeartbeatRegistry.delete(userId);
    }
  };
};

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSessionExpired, setShowSessionExpired] = useState(false);
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState('');
  const [sessionExpiredRole, setSessionExpiredRole] = useState(null); // save role before user is cleared
  const logoutInProgressRef = useRef(false); // prevents session validation from showing modal during manual logout
  // The auth interceptor is installed once on mount, so it cannot close over the
  // latest `user` state. This ref gives it a live view without re-installing.
  const userRef = useRef(null);

  const [permissions, setPermissions] = useState({});

  useEffect(() => {
    // Load user from localStorage
    const savedUser = localStorage.getItem('dws_user');
    if (savedUser) {
      const parsedUser = JSON.parse(savedUser);
      setUser(parsedUser);
      setAxiosAuthToken(parsedUser.token);
      authenticateRealtime(parsedUser.firebase_token || parsedUser.firebaseToken);
      if (parsedUser.role === 'super_admin' || parsedUser.role === 'admin') {
        localStorage.setItem('dws_last_role', 'admin');
      } else if (parsedUser.role && (parsedUser.role.endsWith('_handler') || parsedUser.role.includes('handler'))) {
        localStorage.setItem('dws_last_role', 'handler');
      } else if (parsedUser.role === 'customer' && !parsedUser.is_guest) {
        localStorage.setItem('dws_last_role', 'customer');
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    if (!user) {
      setPermissions({});
      return;
    }

    if (user.role === 'super_admin') {
      setPermissions({
        can_delete_chat: true,
        can_block_clients: true,
        can_send_broadcast: true,
        can_manage_groups: true,
        can_export_reports: true
      });
      return;
    }

    if (user.role === 'customer' || user.is_guest) {
      setPermissions({});
      return;
    }

    const fetchPermissions = async () => {
      try {
        const response = await axios.get(`${API}/settings/permissions`);
        setPermissions(response.data || {});
      } catch (err) {
        console.error('Failed to load permissions:', err);
      }
    };
    fetchPermissions();
  }, [user]);

  // Access tokens are short-lived, so an expired one is the normal state between
  // refreshes rather than an error. The interceptor swaps in a fresh token and
  // replays the request; only a failed refresh means the session is really gone.
  useEffect(() => {
    const interceptorId = installAuthInterceptor({
      onRefreshed: (updated) => {
        setUser(updated);
        // Firebase custom tokens are short-lived too, so re-assert the realtime
        // identity whenever the access token is refreshed.
        authenticateRealtime(updated.firebase_token || updated.firebaseToken);

        // Keep inquiry_session in sync if active guest user token was refreshed
        const inquirySessionStr = localStorage.getItem('inquiry_session');
        if (inquirySessionStr) {
          try {
            const inqSession = JSON.parse(inquirySessionStr);
            if (inqSession.guest_user_id === updated.id) {
              localStorage.setItem('inquiry_session', JSON.stringify({
                ...inqSession,
                token: updated.token,
                refresh_token: updated.refresh_token || updated.refreshToken,
                firebase_token: updated.firebase_token || updated.firebaseToken
              }));
            }
          } catch (e) {}
        }
      },
      onRefreshFailed: () => {
        if (logoutInProgressRef.current) return;
        setSessionExpiredRole(userRef.current?.role);
        signOutRealtime();
        setUser(null);
        localStorage.removeItem('dws_user');
        localStorage.removeItem('dws_session_id');
        localStorage.removeItem('inquiry_session');
        setAxiosAuthToken(null);
        setSessionExpiredMessage('Your session has expired. Please login again.');
        setShowSessionExpired(true);
      }
    });

    return () => axios.interceptors.response.eject(interceptorId);
  }, []);

  // Heartbeat: Update last_seen every 30 seconds
  useEffect(() => {
    if (!user?.id) return;
    return acquireActivityHeartbeat(user.id);
  }, [user]);

  // Track logout when user closes browser/tab
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (!user?.id || !user?.token) return;
      // navigator.sendBeacon cannot set an Authorization header, so the tracking
      // endpoint rejected it. fetch with keepalive survives page unload *and*
      // carries the bearer token.
      fetch(`${API}/admin/activity/track`, {
        method: 'POST',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.token}`
        },
        body: JSON.stringify({ action: 'logout' })
      }).catch(() => {
        // Best-effort: the page is going away regardless.
      });
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [user]);

  const login = useCallback((userData) => {
    setUser(userData);
    setAxiosAuthToken(userData.token);
    authenticateRealtime(userData.firebase_token || userData.firebaseToken);
    localStorage.setItem('dws_user', JSON.stringify(userData));
    // Store session_id separately
    if (userData.session_id) {
      localStorage.setItem('dws_session_id', userData.session_id);
    }
    // Remember role for future logout & PWA launch
    if (userData.role === 'super_admin' || userData.role === 'admin') {
      localStorage.setItem('dws_last_role', 'admin');
    } else if (userData.role && (userData.role.endsWith('_handler') || userData.role.includes('handler'))) {
      localStorage.setItem('dws_last_role', 'handler');
    } else if (userData.role === 'customer' && !userData.is_guest) {
      localStorage.setItem('dws_last_role', 'customer');
    }
  }, []);


  const logout = async (isManual = true) => {
    logoutInProgressRef.current = true; // Block session validation modal immediately
    
    const lastRole = localStorage.getItem('dws_last_role');
    const userRole = user?.role || lastRole;
    const userId = user?.id;
    const sessionId = localStorage.getItem('dws_session_id');
    
    // Clear local state and storage immediately (keep dws_last_role for role-aware redirect)
    signOutRealtime();
    setUser(null);
    localStorage.removeItem('dws_user');
    localStorage.removeItem('dws_session_id');
    setAxiosAuthToken(null);
    
    // Redirect based on logout type
    if (isManual) {
      const loginRoute = (userRole === 'super_admin' || userRole === 'admin') ? '/admin-login' 
        : (userRole === 'handler' || ['deposit_handler', 'withdrawal_handler', 'support_handler', 'inquiry_handler'].includes(userRole) || userRole?.endsWith('_handler') || userRole?.includes('handler')) ? '/handler-login'
        : '/customer-login';
      window.location.href = loginRoute;
    }

    // Fire-and-forget backend tracking in the background
    if (userId) {
      axios.post(`${API}/admin/activity/track`, {
        user_id: userId,
        action: 'logout'
      }).catch(err => console.warn('Activity track failed:', err));
      
      if (sessionId) {
        axios.post(`${API}/auth/logout`, null, {
          params: { session_id: sessionId }
        }).catch(err => console.warn('Logout failed:', err));
      }
    }
  };

  useEffect(() => {
    if (!user?.id) return;
    if (user?.is_guest) return; // Skip session validation for guest support users

    const validateSession = async () => {
      const sessionId = localStorage.getItem('dws_session_id');
      if (!sessionId) {
        if (logoutInProgressRef.current || !localStorage.getItem('dws_user')) return;
        setSessionExpiredRole(user?.role);
        await logout(false);
        setSessionExpiredMessage('Session not found. Please login again.');
        setShowSessionExpired(true);
        return;
      }

      try {
        const response = await axios.post(`${API}/auth/validate-session`, null, {
          params: { session_id: sessionId }
        });

        if (!response.data.valid) {
          if (logoutInProgressRef.current || !localStorage.getItem('dws_user')) return;
          setSessionExpiredRole(user?.role);
          await logout(false);
          setSessionExpiredMessage('Your session has expired or you have logged in from another device.');
          setShowSessionExpired(true);
        }
      } catch (error) {
        console.error('Session validation failed:', error);
      }
    };

    // Validate immediately on mount
    validateSession();

    // Then validate every 15 seconds
    const interval = setInterval(validateSession, 15000);

    return () => clearInterval(interval);
  }, [user]);

  const updateUser = (updates) => {
    const updated = { ...user, ...updates };
    setUser(updated);
    localStorage.setItem('dws_user', JSON.stringify(updated));
  };

  const handleSessionExpiredClose = () => {
    setShowSessionExpired(false);
    const lastRole = localStorage.getItem('dws_last_role');
    const userRole = sessionExpiredRole || user?.role || lastRole || 'customer';
    const loginRoute = (userRole === 'super_admin' || userRole === 'admin') ? '/admin-login' 
      : (userRole === 'handler' || ['deposit_handler', 'withdrawal_handler', 'support_handler', 'inquiry_handler'].includes(userRole) || userRole?.endsWith('_handler') || userRole?.includes('handler')) ? '/handler-login'
      : '/customer-login';
    setSessionExpiredRole(null);
    window.location.href = loginRoute;
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, updateUser, loading, permissions }}>
      {children}
      <SessionExpiredModal 
        show={showSessionExpired}
        message={sessionExpiredMessage}
        onClose={handleSessionExpiredClose}
      />
    </AuthContext.Provider>
  );
};

